import { useEffect, useRef, useState } from "react";
import { Chart } from "chart.js/auto";
import {
  drawNonOverlappingLabel,
  getInitialJitter,
} from "../../utils/drawNonOverlappingLabel";

const GraphExercise = ({ exerciseData, exerciseName }) => {
  const chartRef = useRef(null);
  const [viewMode, setViewMode] = useState("weight");

  useEffect(() => {
    if (exerciseData.length > 0) {
      const sortedData = [...exerciseData].sort(
        (a, b) => new Date(a.combineDate) - new Date(b.combineDate),
      );

      const ctx = chartRef.current.getContext("2d");
      if (chartRef.current.chart) {
        chartRef.current.chart.destroy();
      }

      const isBenchPress = exerciseName?.toLowerCase() === "bench press";
      const getResult = (entry) =>
        isBenchPress && viewMode === "reps" ? (entry.reps ?? 0) : entry.result;

      const results = sortedData.map(getResult);
      const unit = sortedData[0]?.unit?.toLowerCase();
      const improvementIsHigher = unit !== "seconds";
      const minResult = Math.max(Math.min(...results) - 2, 0);
      const maxResult = Math.max(...results) + 2;

      const customLabelsPlugin = {
        id: "customLabels",
        afterDatasetsDraw(chart) {
          const { ctx } = chart;
          const dataset = chart.data.datasets[0];
          const meta = chart.getDatasetMeta(0);

          const placed = [];

          ctx.save();

          for (let i = 0; i < meta.data.length; i++) {
            const point = meta.data[i];
            const value = dataset.data[i];
            if (value == null) continue;

            const x = point.x;
            const jitter = getInitialJitter(meta, i);
            const y = point.y - 12 + jitter;

            drawNonOverlappingLabel(ctx, value.toFixed(3), x, y, placed, {
              minY: 8,
              stepY: 14,
            });
          }

          ctx.restore();
        },
      };

      chartRef.current.chart = new Chart(ctx, {
        type: "line",
        data: {
          labels: sortedData.map((entry) =>
            new Date(entry.combineDate).toLocaleDateString(),
          ),
          datasets: [
            {
              label: "Result",
              data: results,
              backgroundColor: "rgba(0, 0, 255, 0.2)",
              pointRadius: 0,
              tension: 0.4,
              cubicInterpolationMode: "monotone",
              segment: {
                borderColor: (ctx) => {
                  const { p0, p1 } = ctx;
                  const val0 = p0.parsed.y;
                  const val1 = p1.parsed.y;
                  if (val1 === val0) return "black";
                  const isImproved = improvementIsHigher
                    ? val1 > val0
                    : val1 < val0;
                  return isImproved ? "green" : "red";
                },
              },
            },
            {
              label: "Points Highlight",
              data: results.map((val) => val + (maxResult - minResult) * 0.02),
              borderColor: "transparent",
              backgroundColor: "rgba(0, 0, 255, 0.7)",
              pointStyle: "circle",
              pointRadius: sortedData.map((entry, idx) =>
                results[idx] === Math.max(...results) ? 8 : 5,
              ),
              pointHoverRadius: 10,
              pointBorderColor: "white",
              pointBorderWidth: 2,
              showLine: false,
            },
          ],
        },
        options: {
          responsive: true,
          interaction: {
            mode: "index",
            intersect: false,
          },
          layout: {
            padding: {
              top: 30,
            },
          },
          animation: {
            duration: 800,
            easing: "easeOutElastic",
          },
          plugins: {
            title: { display: false },
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  if (tooltipItem.datasetIndex !== 0) return "";
                  const entry = sortedData[tooltipItem.dataIndex];
                  const value = results[tooltipItem.dataIndex];
                  const prev = results[tooltipItem.dataIndex - 1];
                  const delta = typeof prev === "number" ? value - prev : null;
                  const icon =
                    delta === 0
                      ? "⚫"
                      : improvementIsHigher
                        ? delta > 0
                          ? "🔺"
                          : "🔻"
                        : delta < 0
                          ? "🔺"
                          : "🔻";
                  const base = `${value} ${entry.unit}`;
                  const visualHint = delta !== null ? ` ${icon}` : "";
                  const labelSet = isBenchPress
                    ? [
                        `Combine: ${entry.combineName}`,
                        `${base}${visualHint}`,
                        `Reps: ${entry.reps}`,
                      ]
                    : [`Combine: ${entry.combineName}`, `${base}${visualHint}`];
                  return labelSet;
                },
              },
            },
          },
          scales: {
            x: {
              offset: true,
              ticks: { padding: 10 },
              grid: { drawOnChartArea: false },
              title: { display: true, text: "Events" },
            },
            y: {
              type: "linear",
              display: true,
              position: "left",
              min: minResult,
              max: maxResult,
              ticks: {
                stepSize: 1,
                callback: (value) => value.toFixed(0),
              },
              grid: { drawOnChartArea: false },
              title: {
                display: true,
                text: isBenchPress
                  ? viewMode === "weight"
                    ? "lbs"
                    : "Reps"
                  : `${unit || "Units"}`,
                font: { size: 14, weight: "bold" },
              },
            },
          },
        },
        plugins: [customLabelsPlugin],
      });
    }
  }, [exerciseData, viewMode, exerciseName]);

  const sortedData = [...exerciseData].sort(
    (a, b) => new Date(a.combineDate) - new Date(b.combineDate),
  );

  const isBenchPress = exerciseName?.toLowerCase() === "bench press";
  const getResult = (entry) =>
    isBenchPress && viewMode === "reps" ? (entry.reps ?? 0) : entry.result;

  const firstResult = sortedData.length ? getResult(sortedData[0]) : null;
  const lastResult = sortedData.length ? getResult(sortedData.at(-1)) : null;

  const delta =
    firstResult !== null && lastResult !== null
      ? lastResult - firstResult
      : null;

  const normalizedDelta =
    exerciseData[0]?.unit?.includes("second") && Math.abs(delta) > 10
      ? delta / 1000
      : delta;

  const improvementIsHigher = sortedData[0]?.unit !== "seconds";

  const getImprovementStatus = (delta) => {
    if (delta === 0) return "same";
    return improvementIsHigher
      ? delta > 0
        ? "improved"
        : "worse"
      : delta < 0
        ? "improved"
        : "worse";
  };

  const isImproved = getImprovementStatus(delta);

  const deltaColorMap = {
    improved: "text-green-600",
    worse: "text-red-600",
    same: "text-black",
  };

  const deltaColor = deltaColorMap[isImproved];

  const formatDelta = (value) =>
    Number.isInteger(value) ? value.toString() : value.toFixed(3);

  return (
    <div className="flex flex-col items-center mt-6">
      <h1 className="text-l mb-4 text-center">Exercise History</h1>

      {isBenchPress && (
        <div className="flex justify-end gap-2 mb-2 w-full max-w-4xl">
          <button
            onClick={() => setViewMode("weight")}
            className={`px-3 py-1 rounded-full border ${
              viewMode === "weight"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-800"
            }`}
          >
            Weight
          </button>
          <button
            onClick={() => setViewMode("reps")}
            className={`px-3 py-1 rounded-full border ${
              viewMode === "reps"
                ? "bg-blue-600 text-white"
                : "bg-gray-200 text-gray-800"
            }`}
          >
            Reps
          </button>
        </div>
      )}

      <div className="overflow-x-auto w-full max-w-4xl">
        <canvas ref={chartRef}></canvas>
      </div>

      {delta !== null && (
        <div className={`mt-2 text-center font-semibold ${deltaColor}`}>
          Overall change: {formatDelta(Math.abs(normalizedDelta))}{" "}
          {viewMode === "reps" ? "reps" : exerciseData[0]?.unit}
        </div>
      )}
    </div>
  );
};

export default GraphExercise;
